import { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslations } from '@altermaind/core/i18n';
import { Box, Button, Svg, Typography } from '@altermaind/web/ui';
import { FeatureFlag } from '@/_shared/constants';
import { PLATFORM_TYPE_FROM_ROLE } from '@/_shared/constants/platformType';
import { Routes } from '@/_shared/constants/routes';
import { useFeatureFlags, usePlatform } from '@/_shared/hooks';
import { useDialog } from '@/_shared/hooks/useDialog';
import { getOrganizationsCall, getUserProfileCall } from '@/core/state/api';
import { getEmployerCreditsCall } from '@/credits/state';
import { InitialsAvatar } from '../InitialsAvatar';
import { LanguageSelector } from '../LanguageSelector';
import { RenderLocalesWithComponents } from '../RenderLocalesWithComponents';
import { SwitchOrganization } from '../SwitchOrganization';
import { SwitchPlatform } from './SwitchPlatform';
import { UserInfoComponent } from './UserProfileCard';

enum SubMenuContent {
  DEFAULT = 'default',
  SWITCH_ORGANIZATION = 'switchOrganization',
  SWITCH_PLATFORM = 'switchPlatform',
}

export const UserInfo = () => {
  const t = useTranslations('general');
  const navigate = useNavigate();
  const { isFeatureEnabled } = useFeatureFlags();
  const creditsFF = isFeatureEnabled(FeatureFlag.CREDITS);
  const isFFSwitchOrganization = isFeatureEnabled(FeatureFlag.MULTI_ORGANIZATION);
  const [menuContent, setMenuContent] = useState<SubMenuContent>(SubMenuContent.DEFAULT);
  const { openDialog: openDialogLogout } = useDialog('logout');
  const { openDialog: openDialogCredits } = useDialog('creditsUser');
  const { fullName, userRole, activeContext, hasMultipleContexts } = getUserProfileCall.hook();
  const { totalAvailableCredits } = getEmployerCreditsCall.hook();
  const { data: organizations, defaultOrganization } = getOrganizationsCall.hook();
  const { isAdmin, isLearning } = usePlatform();

  const hasMultipleOrganizations = organizations.length > 1;

  const goToSwitchOrganization = useCallback(() => {
    setMenuContent(SubMenuContent.SWITCH_ORGANIZATION);
  }, []);

  const goToSwitchPlatform = useCallback(() => {
    setMenuContent(SubMenuContent.SWITCH_PLATFORM);
  }, []);

  const goToProfile = useCallback(() => {
    navigate(Routes.Profile);
    const element = document.querySelector('[data-testId="user-logo"]');
    if (element) {
      (element as HTMLElement).click();
    }
  }, [navigate]);

  const isFFLanguageSelector = isFeatureEnabled(FeatureFlag.LANGUAGE_SELECTOR);

  const restoreDefaultMenu = useCallback(() => {
    setMenuContent(SubMenuContent.DEFAULT);
  }, []);

  const renderMenuContent = () => {
    if (menuContent === SubMenuContent.SWITCH_PLATFORM) {
      return <SwitchPlatform goBack={restoreDefaultMenu} />;
    }

    if (menuContent === SubMenuContent.SWITCH_ORGANIZATION) {
      return <SwitchOrganization goBack={restoreDefaultMenu} />;
    }

    return (
      <Box
        bg='grey/dark/background'
        p='s'
        display='flex'
        flexDirection='column'
        borderRadius='l'
        boxShadow='0px 0px 16px 0px #00000066'
        minWidth={230}
        justifyContent='center'
        alignItems='center'
      >
        <InitialsAvatar text={fullName} />
        <Typography variant='p2' mt='xxs'>
          {fullName}
        </Typography>
        {isAdmin && (
          <Typography variant='p2/light' fontSize='xs'>
            {userRole}
          </Typography>
        )}
        {isLearning && creditsFF && (
          <>
            <Box
              mb='xxs'
              mt='xxs'
              borderRadius='xl'
              px='xxs'
              borderColor='white'
              border='1px solid'
            >
              <Box display='flex' flexDirection='row' alignItems='center'>
                <Svg name='credit' size='xs' />
                <Typography ml='xxs' variant='h3' fontSize='m'>
                  <Typography as='span' fontWeight='bold'>
                    {totalAvailableCredits}
                  </Typography>{' '}
                  {t('switchPlatform.creditLabel')}
                </Typography>
              </Box>
            </Box>
            <Typography
              style={{ cursor: 'pointer' }}
              onClick={openDialogCredits}
              variant='p2/light'
              fontSize='xs'
            >
              <RenderLocalesWithComponents
                text={t('switchPlatform.openCreditsModal', {
                  date: '!{CREDITS}!',
                })}
                placeholders={{
                  '!{CREDITS}!': (
                    <Typography
                      as='span'
                      style={{ textDecoration: 'underline' }}
                      variant='p2/light'
                    >
                      {t('switchPlatform.openCreditsModalLabel')}
                    </Typography>
                  ),
                }}
              />
            </Typography>
          </>
        )}
        {hasMultipleContexts && (
          <Box mt='xs' width='100%'>
            <Typography
              variant='p3/light'
              fontWeight='300'
              textAlign='left'
              fontSize='xxs'
              mb='xxxs'
            >
              {t('platform')}
            </Typography>
            <Box
              borderBottom='1px solid'
              pl='xxs'
              pb='xs'
              borderColor='grey/light/background'
              display='flex'
              alignItems='center'
              alignContent='center'
              onClick={goToSwitchPlatform}
              style={{ cursor: 'pointer' }}
            >
              <Svg name='book' size='xs' />
              <Typography ml='xs' variant='p2/light' fontSize='xs' flex={1}>
                {userRole ? t(`switchPlatform.${PLATFORM_TYPE_FROM_ROLE[userRole]}`) : ''}
              </Typography>
              <Svg name='chevronRight' size='xs' />
            </Box>
          </Box>
        )}
        {isFFSwitchOrganization && hasMultipleOrganizations && (
          <Box mt='xs' width='100%'>
            <Typography
              variant='p3/light'
              fontWeight='300'
              textAlign='left'
              fontSize='xxs'
              mb='xxxs'
            >
              {t('organization')}
            </Typography>
            <Box
              borderBottom='1px solid'
              pl='xxs'
              pb='xs'
              borderColor='grey/light/background'
              display='flex'
              alignItems='center'
              alignContent='center'
              onClick={goToSwitchOrganization}
              style={{ cursor: 'pointer' }}
            >
              <Svg name='company' size='xs' />
              <Typography ml='xs' variant='p2/light' fontSize='xs' flex={1}>
                {defaultOrganization}
              </Typography>
              <Svg name='chevronRight' size='xs' />
            </Box>
          </Box>
        )}
        {isFFLanguageSelector && (
          <Box mt='xs' width='100%'>
            <Typography
              variant='p3/light'
              fontWeight='300'
              textAlign='left'
              fontSize='xxs'
              mb='xxxs'
            >
              {t('language')}
            </Typography>
            <Box
              borderBottom='1px solid'
              pb='xs'
              borderColor='grey/light/background'
              display='flex'
              alignItems='center'
              alignContent='center'
              style={{ cursor: 'pointer' }}
            >
              <LanguageSelector />
            </Box>
          </Box>
        )}
        {isLearning && (
          <Button onClick={goToProfile} mt='xs' fontSize='s' variant='primary'>
            {t('switchPlatform.viewProfile')}
          </Button>
        )}
        <Typography variant='p2' onClick={openDialogLogout} cursor='pointer' mt='s'>
          <Box display='flex' flexDirection='row' alignItems='center'>
            <Button variant='positive'>
              <Svg name='logout' stroke='primary' size='s' />{' '}
              <Typography fontWeight='500' ml='xxs' as='span' data-testid='logoutCta'>
                {t('logout')}
              </Typography>
            </Button>
          </Box>
        </Typography>
      </Box>
    );
  };

  return (
    <UserInfoComponent
      username={fullName}
      role={t(`switchPlatform.${activeContext}`)}
      backgroundColor='primary'
      onClose={restoreDefaultMenu}
      // eslint-disable-next-line react/no-children-prop
      children={renderMenuContent()}
    />
  );
};
